import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../../assets/vasudharini Homes - Black.png';
import FloatingCallButton from '../ui/FloatingCallButton';
import FloatingWhatsAppButton from '../ui/FloatingWhatsAppButton';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Add scroll detection for navbar style change
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Animation variants
  const menuVariants = {
    closed: { 
      opacity: 0,
      height: 0,
      transition: { 
        duration: 0.3,
        when: "afterChildren",
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    },
    open: { 
      opacity: 1,
      height: "100vh",
      transition: { 
        duration: 0.4,
        when: "beforeChildren",
        staggerChildren: 0.1,
        staggerDirection: 1,
        ease: "easeOut"
      }
    }
  };

  const linkVariants = {
    closed: { y: 20, opacity: 0 },
    open: { y: 0, opacity: 1 }
  };

  return (
    <motion.nav 
      className={`sticky top-0 z-50 bg-white text-black px-2 sm:px-5 ${
        scrolled ? 'shadow-md' : ''
      } transition-all duration-300`}
    >
      <div className="mx-auto">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <motion.img 
              src={logo} 
              alt="Vasudharini" 
              className="w-20 sm:w-24 h-20 sm:h-24 my-auto" 
            />
          </Link>

          <FloatingCallButton phoneNumber="+919036026133" />
          <FloatingWhatsAppButton 
            phoneNumber="+919036026133" 
          />

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-8 font-medium">
            <NavLink 
              to="/" 
              className={({ isActive }) => 
                `relative overflow-hidden group py-2 px-1 ${isActive ? 'text-yellow-400' : 'hover:text-yellow-400'} transition-colors`
              }
            >
              Home
              <motion.span 
                className="absolute bottom-0 left-0 w-full h-0.5 bg-yellow-400 origin-left"
                initial={{ scaleX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.3 }}
              />
            </NavLink>
            <NavLink 
              to="/ongoing-projects" 
              className={({ isActive }) => 
                `relative overflow-hidden group py-2 px-1 ${isActive ? 'text-yellow-400' : 'hover:text-yellow-400'} transition-colors`
              }
            >
              Ongoing Projects
              <motion.span 
                className="absolute bottom-0 left-0 w-full h-0.5 bg-yellow-400 origin-left"
                initial={{ scaleX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.3 }}
              />
            </NavLink>
            <NavLink 
              to="/about" 
              className={({ isActive }) => 
                `relative overflow-hidden group py-2 px-1 ${isActive ? 'text-yellow-400' : 'hover:text-yellow-400'} transition-colors`
              }
            >
              About
              <motion.span 
                className="absolute bottom-0 left-0 w-full h-0.5 bg-yellow-400 origin-left"
                initial={{ scaleX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.3 }}
              />
            </NavLink>
            <NavLink 
              to="/contact" 
              className={({ isActive }) => 
                `relative overflow-hidden group py-2 px-1 ${isActive ? 'text-yellow-400' : 'hover:text-yellow-400'} transition-colors`
              }
            >
              Contact
              <motion.span 
                className="absolute bottom-0 left-0 w-full h-0.5 bg-yellow-400 origin-left"
                initial={{ scaleX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.3 }}
              />
            </NavLink>
          </div>

          {/* CTA Button */}
          <div className="flex items-center">
            {/* Connect Button */}
            <motion.div
              // whileHover={{ scale: 1.05 }}
              // whileTap={{ scale: 0.95 }}
            >
              <Link 
                to="/contact" 
                className="hidden md:block bg-yellow-400 hover:bg-yellow-500 text-black font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Book a Site Visit
              </Link>
            </motion.div>
          </div>

          {/* Mobile Menu Button */}
          <motion.button 
            className="md:hidden text-black focus:outline-none z-50 "
            onClick={toggleMenu}
            whileTap={{ scale: 0.9 }}
          >
            <div className="w-8 h-8 flex flex-col justify-center items-center pe-2">
              <motion.span
                animate={{ 
                  rotate: isMenuOpen ? 45 : 0,
                  y: isMenuOpen ? 8 : 0
                }}
                className={`w-6 h-0.5 ${isMenuOpen ? 'bg-black' : 'bg-current'} rounded-full mb-2`}
                transition={{ duration: 0.3 }}
              />
              <motion.span
                animate={{ 
                  opacity: isMenuOpen ? 0 : 1
                }}
                className={`w-6 h-0.5 ${isMenuOpen ? 'bg-black' : 'bg-current'} rounded-full mb-2`}
                transition={{ duration: 0.3 }}
              />
              <motion.span
                animate={{ 
                  rotate: isMenuOpen ? -45 : 0,
                  y: isMenuOpen ? -8 : 0
                }}
                className={`w-6 h-0.5 ${isMenuOpen ? 'bg-black' : 'bg-current'} rounded-full`}
                transition={{ duration: 0.3 }}
              />
            </div>
          </motion.button>
        </div>

        {/* Full-Height Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              className="md:hidden overflow-hidden fixed inset-0 z-40 bg-white"
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              <div className="flex flex-col justify-center items-center h-full">
                <div className="space-y-6 w-full max-w-sm px-6">
                  <motion.div variants={linkVariants}>
                    <Link 
                      to="/" 
                      className="block text-2xl text-center uppercase hover:text-yellow-400 transition-colors py-3"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Home
                    </Link>
                  </motion.div>
                  <motion.div variants={linkVariants}>
                    <Link 
                      to="/ongoing-projects" 
                      className="block text-2xl text-center uppercase hover:text-yellow-400 transition-colors py-3"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Ongoing Projects
                    </Link>
                  </motion.div>
                  <motion.div variants={linkVariants}>
                    <Link 
                      to="/about" 
                      className="block text-2xl text-center uppercase hover:text-yellow-400 transition-colors py-3"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      About
                    </Link>
                  </motion.div>
                  <motion.div variants={linkVariants}>
                    <Link 
                      to="/contact" 
                      className="block text-2xl text-center uppercase hover:text-yellow-400 transition-colors py-3"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Contact
                    </Link>
                  </motion.div>

                  <motion.div 
                    variants={linkVariants}
                    className="pt-8"
                  >
                    <Link 
                      to="/contact" 
                      className="block bg-yellow-400 hover:bg-yellow-500 text-black font-medium text-lg py-4 px-6 rounded-lg transition-colors text-center"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Book a Site Visit
                    </Link>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;